/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid } from '@coursera/cds-core';

import EnterpriseOrgScopedSearchBarGridItem from 'bundles/composable-page-header-enterprise/components/EnterpriseOrgScopedSearchBarGridItem';
import type { UseEnterpriseMyPageHeaderResult as MyEnterprisePageHeader } from 'bundles/composable-page-header-enterprise/contexts/EnterpriseMyPageHeaderContext';
import DefaultLoggedInEnterpriseMobileMenuConfig from 'bundles/composable-page-header-enterprise/module-renderers/DefaultLoggedInEnterpriseMobileMenuConfig';
import {
  getEnterpriseOrgLogo,
  getEnterpriseOrgLogoLabel,
  getLinkForOrgHome,
} from 'bundles/composable-page-header-enterprise/utils/enterpriseLogoUtils';
import MainNavigationWrapper from 'bundles/composable-page-header/components/MainNavigationWrapper';
import NotificationsLegacyGridItem from 'bundles/composable-page-header/components/grid-items/NotificationsLegacyGridItem';
import CourseraHomeLogo from 'bundles/composable-page-header/components/grid-items/home-logo/CourseraHomeLogo';
import HomeLogoGridItemModule from 'bundles/composable-page-header/components/grid-items/home-logo/HomeLogoGridItemModule';
import MobileAccountMenuGridItem from 'bundles/composable-page-header/components/grid-items/mobile/MobileAccountMenuGridItem';
import MobileMenuIconGridItem from 'bundles/composable-page-header/components/grid-items/mobile/MobileMenuIconGridItem';
import AccountMenuConfig from 'bundles/composable-page-header/components/mobile-menu-items/AccountMenuConfig';

const styles = {
  fullContainer: css`
    flex-wrap: nowrap;
  `,
  leftSide: css`
    flex-wrap: nowrap;
  `,
  rightSide: css`
    flex-wrap: nowrap;
  `,
};

type Props = {
  pageHeaderData: MyEnterprisePageHeader;
  hideSearch?: boolean;
};

const LoggedInOrgScopedEnterpriseMobileMainNav = ({ pageHeaderData, hideSearch }: Props) => {
  const {
    enrolledDegreePrograms,
    enrolledEnterprisePrograms,
    chosenDegree,
    chosenEnterpriseProgram,
    hasEnrolledPrograms,
    naptimeManagedThirdPartyOrganizations,
    hasCheatingIncidents,
    canViewEducatorAdminLink,
    naptimeCurrentPageOrganization,
    naptimeChosenOrganization,
    naptimeOrganizationsWithProgramEnrollments,
    isCourseraPlusLearner,
  } = pageHeaderData;

  const currentOrganization = naptimeCurrentPageOrganization ?? naptimeChosenOrganization;
  const logo = currentOrganization ? [getEnterpriseOrgLogo(currentOrganization)] : [<CourseraHomeLogo key="1" />];

  const LogoComponent = (
    <HomeLogoGridItemModule
      href={currentOrganization ? getLinkForOrgHome(currentOrganization) : '/'}
      logos={logo}
      ariaLabel={currentOrganization ? getEnterpriseOrgLogoLabel(currentOrganization) : undefined}
    />
  );

  return (
    <MainNavigationWrapper>
      <Grid item container css={styles.fullContainer}>
        <Grid item container alignItems="center" css={styles.leftSide}>
          <MobileMenuIconGridItem
            menuConfig={
              <DefaultLoggedInEnterpriseMobileMenuConfig
                currentOrganization={currentOrganization}
                enrolledOrganizations={naptimeOrganizationsWithProgramEnrollments}
                enrolledDegreePrograms={enrolledDegreePrograms}
                enrolledEnterprisePrograms={enrolledEnterprisePrograms}
                chosenDegree={chosenDegree}
                chosenEnterpriseProgram={chosenEnterpriseProgram}
                hasEnrolledPrograms={!!hasEnrolledPrograms}
                naptimeManagedThirdPartyOrganizations={naptimeManagedThirdPartyOrganizations}
                hasCheatingIncidents={hasCheatingIncidents}
                canViewEducatorAdminLink={canViewEducatorAdminLink}
              />
            }
            homeLogo={LogoComponent}
          />
          {LogoComponent}
        </Grid>
        <Grid item container alignItems="center" justifyContent="flex-end" css={styles.rightSide}>
          <NotificationsLegacyGridItem />
          {!hideSearch && (
            <EnterpriseOrgScopedSearchBarGridItem
              thirdPartyOrganizationSlug={naptimeCurrentPageOrganization?.slug}
              enrolledEnterprisePrograms={enrolledEnterprisePrograms}
            />
          )}
          <MobileAccountMenuGridItem
            isCourseraPlusLearner={!!isCourseraPlusLearner}
            menuConfig={
              <AccountMenuConfig
                hasEnrolledPrograms={!!hasEnrolledPrograms}
                naptimeManagedThirdPartyOrganizations={naptimeManagedThirdPartyOrganizations}
                canViewEducatorAdminLink={canViewEducatorAdminLink}
                hasCheatingIncidents={hasCheatingIncidents}
                currentOrganization={currentOrganization}
                degrees={enrolledDegreePrograms}
                organizations={naptimeOrganizationsWithProgramEnrollments}
              />
            }
          />
        </Grid>
      </Grid>
    </MainNavigationWrapper>
  );
};

export default LoggedInOrgScopedEnterpriseMobileMainNav;
